import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import {
  getUncatchEndpointData,
  getAlimentadorIdsByComunaId,
  getGeoJSONByAlimentadorIds
} from '../../utils/request';
import Map from '../../components/maps/Map';
import { SimpleObject } from '../../types';
import GenericIcons from '../../components/generics/GenericIcons';
import SelectInput, { SelectDataType } from '../../components/SelectInput';
import CheckBox from '../../components/CheckBox';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { DEFAULT_REGION_NOMBRE, DEFAULT_COMUNA_NOMBRE } from '../../constants/mapDefaults';
import '../../css/PublicMap.css';

const PublicMapPage: FunctionComponent = () => {
  const isInitialMount = useRef(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [isHoveredTable, setIsHoveredTable] = useState<boolean>(false);
  const [leftSidebarVisible, setLeftSidebarVisible] = useState<boolean>(true);
  const [rightSidebarVisible, setRightSidebarVisible] = useState<boolean>(true);
  const [localMapCenter, setLocalMapCenter] = useState<
    | {
        lat: number;
        lng: number;
      }
    | undefined
  >(undefined);
  const [clickedArtifact, setClickedArtifact] = useState<SimpleObject>({});
  const [regiones, setRegiones] = useState<Array<SelectDataType>>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [comunas, setComunas] = useState<Array<SelectDataType>>([]);
  const [selectedComuna, setSelectedComuna] = useState<string>('');
  const [selectors, setSelectors] = useState<
    Array<{
      checked: boolean;
      icon: string;
      label: string;
      key: string;
      disabled?: boolean;
    }>
  >([
    {
      checked: true,
      icon: 'alimentador',
      key: 'alimentador',
      label: 'Alimentador'
    },
    {
      checked: true,
      icon: 'transformador',
      key: 'Transformador de distribucion',
      label: 'Transformador secundario (MT/BT)'
    },
    {
      checked: false,
      icon: 'subestacion',
      key: 'subestacion',
      label: 'Subestación (AT/MT)',
      disabled: true
    },
    {
      checked: true,
      icon: 'linea',
      key: 'linea',
      label: 'Líneas'
    },
    {
      checked: false,
      icon: 'pmgd',
      key: 'pmgd',
      label: 'PMGD',
      disabled: true
    },
    {
      checked: false,
      icon: 'consumo',
      key: 'consumo',
      label: 'Consumo',
      disabled: true
    },
    {
      checked: false,
      icon: 'techoFv',
      key: 'techoFv',
      label: 'Techo FV',
      disabled: true
    },
    {
      checked: false,
      icon: 'gd',
      key: 'gd',
      label: 'Generación no solar',
      disabled: true
    },
    {
      checked: false,
      icon: 'poste',
      key: 'Poste',
      label: 'Postes'
    },
    {
      checked: true,
      icon: 'equipoOperacionControl',
      key: 'Equipo de operacion y control',
      label: 'Equipo de operación y control'
    }
  ]);

  const [mapPoints, setMapPoints] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);
  const [mapLines, setMapLines] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);
  const [mapPolygons, setMapPolygons] = useState<Feature<Geometry, GeoJsonProperties>[]>([]);

  const [state, setState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    loading: true as boolean
  });

  useEffect(() => {
    setMapPoints(state.mapPointFeatures);
    setMapLines(state.mapLineFeatures);
    setMapPolygons(state.mapPolygonFeatures);
  }, [state]);

  useEffect(() => {
    const feederChecked = selectors.find((s) => s.key === 'alimentador')?.checked;
    if (feederChecked) {
      setMapPolygons(state.mapPolygonFeatures);
    } else {
      setMapPolygons([]);
    }
    const linesChecked = selectors.find((s) => s.key === 'linea')?.checked;
    if (linesChecked) {
      setMapLines(state.mapLineFeatures);
    } else {
      setMapLines([]);
    }

    const selectorKeys = [
      'Transformador de distribucion',
      'Poste',
      'Equipo de operacion y control'
    ];

    const artefactoCheckedMap = selectorKeys.reduce((acc, key) => {
      const checked = selectors.find((s) => s.key === key)?.checked;
      if (checked) {
        acc[key] = true;
      }
      return acc;
    }, {} as Record<string, boolean>);

    setMapPoints(
      state.mapPointFeatures.filter((obj: any) => artefactoCheckedMap[obj?.properties?.artefacto])
    );
  }, [selectors, state]);

  // --- FLUJO ANTERIOR: carga completa al montar (comentado, reemplazado por carga por comuna) ---
  // const pointFeaturesReq = getGeoJSONChunked('mapElements/geoJSON/points');
  // const lineFeaturesReq = getGeoJSONChunked('mapElements/geoJSON/lines');
  // const polygonFeaturesReq = getGeoJSONChunked('mapElements/geoJSON/polygons');
  // const alimentadoresReq = getUncatchEndpointData({ endpoint: 'mapElements/alimentadores' });
  // const responses = await Promise.all([pointFeaturesReq, lineFeaturesReq, polygonFeaturesReq, alimentadoresReq]);
  // --- FIN FLUJO ANTERIOR ---

  useEffect(() => {
    const init = async () => {
      try {
        const regionesReq = await getUncatchEndpointData({ endpoint: 'region' });
        if (regionesReq.length > 0) {
          setRegiones(
            regionesReq.map((row: { regionId: string; regionNombre: string }) => ({
              value: row.regionId,
              name: row.regionNombre
            }))
          );
          const defaultRegion = regionesReq.find(
            (r: { regionNombre: string }) => r.regionNombre === DEFAULT_REGION_NOMBRE
          );
          if (defaultRegion) {
            setSelectedRegion(defaultRegion.regionId);
          } else {
            console.warn(`Región por defecto "${DEFAULT_REGION_NOMBRE}" no encontrada en BD`);
            isInitialMount.current = false;
            setState((prev) => ({ ...prev, loading: false }));
            setLoading(false);
          }
        } else {
          isInitialMount.current = false;
          setState((prev) => ({ ...prev, loading: false }));
          setLoading(false);
        }
      } catch (error) {
        console.log(error);
        isInitialMount.current = false;
        setState((prev) => ({ ...prev, loading: false }));
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const getData = async () => {
      setLoading(true);
      setSelectedComuna('');
      try {
        if (!selectedRegion) {
          setComunas([]);
          return;
        }

        const region = await getUncatchEndpointData({
          endpoint: 'region/getRegion',
          query: { regionId: selectedRegion }
        });
        if (cancelled) return;
        if (region.length > 0) {
          setLocalMapCenter({
            lng: region[0].longitud,
            lat: region[0].latitud
          });
        }

        const comunasReq = await getUncatchEndpointData({
          endpoint: 'comuna',
          query: { regionId: selectedRegion }
        });
        if (cancelled) return;
        if (comunasReq.length > 0) {
          setComunas(
            comunasReq.map((row: { comunaId: string; comunaNombre: string }) => ({
              value: row.comunaId,
              name: row.comunaNombre
            }))
          );
          if (isInitialMount.current) {
            const defaultComuna = comunasReq.find(
              (c: { comunaNombre: string }) => c.comunaNombre === DEFAULT_COMUNA_NOMBRE
            );
            if (defaultComuna) {
              setSelectedComuna(defaultComuna.comunaId);
            } else {
              console.warn(`Comuna por defecto "${DEFAULT_COMUNA_NOMBRE}" no encontrada`);
              setState((prev) => ({ ...prev, loading: false }));
            }
            isInitialMount.current = false;
          }
        } else {
          setComunas([]);
          if (isInitialMount.current) {
            isInitialMount.current = false;
            setState((prev) => ({ ...prev, loading: false }));
          }
        }
      } catch (error) {
        console.log(error);
        if (isInitialMount.current) {
          isInitialMount.current = false;
          setState((prev) => ({ ...prev, loading: false }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    getData();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  useEffect(() => {
    let cancelled = false;
    const loadComuna = async () => {
      if (!selectedComuna) {
        if (!isInitialMount.current) {
          setState((prev) => ({
            ...prev,
            mapPointFeatures: [],
            mapLineFeatures: [],
            mapPolygonFeatures: [],
            electricFeeders: {},
            loading: false
          }));
          setClickedArtifact({});
        }
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const comuna = await getUncatchEndpointData({
          endpoint: 'comuna/getComuna',
          query: { comunaId: selectedComuna }
        });
        if (cancelled) return;
        if (comuna.length > 0) {
          setLocalMapCenter({
            lng: comuna[0].longitud,
            lat: comuna[0].latitud
          });
        }

        const alimentadorIds = await getAlimentadorIdsByComunaId(selectedComuna);
        if (cancelled) return;

        if (alimentadorIds.length === 0) {
          setState((prev) => ({
            ...prev,
            mapPointFeatures: [],
            mapLineFeatures: [],
            mapPolygonFeatures: [],
            electricFeeders: {},
            loading: false
          }));
          return;
        }

        const [pointFeatures, lineFeatures, polygonFeatures, allAlimentadores] = await Promise.all([
          getGeoJSONByAlimentadorIds('points', alimentadorIds),
          getGeoJSONByAlimentadorIds('lines', alimentadorIds),
          getGeoJSONByAlimentadorIds('polygons', alimentadorIds),
          getUncatchEndpointData({ endpoint: 'mapElements/alimentadores' })
        ]);
        if (cancelled) return;

        const alimentadorIdSet = new Set(alimentadorIds);
        const filteredAlimentadores = Array.isArray(allAlimentadores)
          ? allAlimentadores.filter((a: any) => alimentadorIdSet.has(a.alimentadorId))
          : allAlimentadores;

        setState((prev) => ({
          ...prev,
          mapPointFeatures: pointFeatures,
          mapLineFeatures: lineFeatures,
          mapPolygonFeatures: polygonFeatures,
          electricFeeders: filteredAlimentadores,
          loading: false
        }));
      } catch (error) {
        console.log(error);
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      }
    };
    loadComuna();
    return () => { cancelled = true; };
  }, [selectedComuna]);

  return (
    <div className='public-map-container'>
      <div className='public-map-location-container'>
        <span className='vdr-icon-tile vdr-map-location-tile' aria-hidden='true'>
          <PlaceOutlinedIcon />
        </span>
        <div className='public-map-location-selector-section-container'>
          <div className='public-map-location-input-container'>
            <label className='primary-label-style'>Región</label>
            <SelectInput
              label='Selecciona Región'
              name='region'
              id='region'
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              options={regiones}
              disabled={loading}
            />
          </div>
          <div className='public-map-location-input-container'>
            <label className='primary-label-style'>Comuna</label>
            <SelectInput
              label='Selecciona Comuna'
              name='commune'
              id='commune'
              value={selectedComuna}
              onChange={(e) => setSelectedComuna(e.target.value)}
              options={comunas}
              disabled={loading}
            />
          </div>
        </div>
      </div>
      {!leftSidebarVisible && (
        <div className='public-map-left-arrow-outside-container'>
          <button
            type='button'
            className='primary-button-style vdr-map-float-button'
            aria-label='Mostrar panel de artefactos'
            onClick={() => setLeftSidebarVisible(true)}>
            <GenericIcons
              icon='arrowLeftMenu'
              className='vdr-map-float-button-icon'
              imageStyle={{ transform: 'rotate(180deg)' }}
            />
          </button>
        </div>
      )}
      <div className='public-map-content-container'>
        {leftSidebarVisible && (
          <div className='public-map-content-left-section'>
            <div
              className='public-map-arrow-inside-container'
              style={{ justifyContent: 'space-between' }}>
              <h3 className='public-map-arrow-inside-text'>Artefactos</h3>
              <div className='public-map-arrow-inside-button-container'>
                <button
                  type='button'
                  className='vdr-map-panel-toggle'
                  aria-label='Ocultar panel de artefactos'
                  onClick={() => setLeftSidebarVisible(false)}>
                  <GenericIcons icon='arrowLeftMenu' className='vdr-map-panel-toggle-icon' />
                </button>
              </div>
            </div>
            <div className='public-map-content-left-checkbox-container'>
              {selectors.map((s, idx) => (
                <CheckBox
                  key={`checkbox-key-${idx}`}
                  label={s.label}
                  checked={s.checked}
                  icon={s.icon}
                  disabled={s.disabled}
                  onChange={(e) => {
                    setSelectors((prevSelectors) =>
                      prevSelectors.map((selector, i) =>
                        i === idx ? { ...selector, checked: e.target.checked } : selector
                      )
                    );
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className='public-map-content-map-section'
          style={{
            gridColumn:
              leftSidebarVisible && rightSidebarVisible
                ? '4/10'
                : leftSidebarVisible && !rightSidebarVisible
                ? '4/13'
                : !leftSidebarVisible && rightSidebarVisible
                ? '1/10'
                : '1/13'
          }}>
          <Map
            mapPointFeatures={mapPoints}
            mapLineFeatures={mapLines}
            mapPolygonFeatures={mapPolygons}
            electricFeeders={state.electricFeeders}
            onArtifactClick={(data) => setClickedArtifact(data)}
            mapCenter={localMapCenter}
          />
          {(loading || state.loading) && (
            <div className='public-map-content-map-loader-container'>
              <span className='loader'></span>
            </div>
          )}
          {!selectedComuna && !state.loading && !loading && (
            <div className='public-map-content-map-overlay-message'>
              Selecciona una región y comuna para ver los datos de la red
            </div>
          )}
        </div>

        {rightSidebarVisible && (
          <div className='public-map-content-right-section'>
            <div className='public-map-arrow-inside-container'>
              <div className='public-map-arrow-inside-button-container'>
                <button
                  type='button'
                  className='vdr-map-panel-toggle'
                  aria-label='Ocultar panel de información técnica'
                  onClick={() => setRightSidebarVisible(false)}>
                  <GenericIcons icon='arrowLeftMenu' className='vdr-map-panel-toggle-icon' />
                </button>
                <p className='public-map-arrow-inside-text'>Info. técnica</p>
              </div>
            </div>
            <div className='public-map-content-table-title-container'>
              <span className='public-map-content-table-title'>INFORMACIÓN TÉCNICA</span>
            </div>
            <table
              className='public-map-content-table-content'
              style={{ zIndex: isHoveredTable ? '9999' : '999' }}
              onMouseEnter={() => setIsHoveredTable(true)}
              onMouseLeave={() => setIsHoveredTable(false)}>
              <thead>
                <tr>
                  <th className='public-map-content-table-column public-map-content-table-header'>
                    Campo
                  </th>
                  <th className='public-map-content-table-column public-map-content-table-header'>
                    Contenido
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(clickedArtifact).length > 0 ? (
                  Object.keys(clickedArtifact)
                    .filter(
                      (key) =>
                        clickedArtifact[key] === null ||
                        typeof clickedArtifact[key] !== 'object'
                    )
                    .map((key) => (
                      <tr key={key}>
                        <td className='public-map-content-table-column'>{key}</td>
                        <td className='public-map-content-table-column'>
                          {clickedArtifact[key]}
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={2} className='public-map-content-table-column-without-data'>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!rightSidebarVisible && (
        <div className='public-map-right-arrow-outside-container'>
          <button
            type='button'
            className='primary-button-style vdr-map-float-button'
            aria-label='Mostrar panel de información técnica'
            onClick={() => setRightSidebarVisible(true)}>
            <GenericIcons icon='arrowLeftMenu' className='vdr-map-float-button-icon' />
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicMapPage;
